/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 6 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation } from "@i18n";
import React, { useState, forwardRef, useCallback, useMemo, useId, useRef, useEffect } from "react";
import { untranslated, type TranslatedString } from "@i18n";
import {
  MenuItem,
  MenuList,
  ListItemText,
  SxProps,
  Theme,
  CircularProgress,
  Popper,
  ClickAwayListener,
  IconButton,
  Typography,
  Divider,
} from "@mui/material";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { cssLengthToPx } from "@CubeComponents/utils/cssLengthToPx";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";
import { AutoSizer, List } from "react-virtualized";
import axiosWrapper from "../../services/axiosWrapper";
import TextFieldComponent from "./TextFieldComponent";
import CheckBoxComponent from "@CubeComponents/Mui_Form/CheckBox";
import MenuItemComponent from "@CubeComponents/html/MenuItemComponent";
import Text from "@CubeComponents/html/Text/Text";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import * as Sentry from "@sentry/react";

// ===== Type Definitions =====

/** Base constraint for option objects - must be a plain object with string keys */
type OptionBase = Record<string, unknown>;

/**
 * Option members are `unknown` (an option is a `Record<string, unknown>`), so anything rendered out
 * of one has to be converted rather than asserted into `ReactNode`.
 */
function toDisplayNode(value: unknown): React.ReactNode {
  if (value === null || value === undefined) return null;
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return value;
  if (typeof value === "object" && React.isValidElement(value)) return value;
  return String(value);
}

/** React keys must be a string or a number; the `fields.value` member of an option is `unknown`. */
function toOptionKey(value: unknown, index: number): React.Key {
  if (typeof value === "string" || typeof value === "number") {
    return value || `item-${index}`;
  }
  return `item-${index}`;
}

/**
 * A selected option identified by its `fields.value` key alone, for callers that hold only the id
 * (e.g. it was persisted) and let the fetched `options` / `remoteDataSource` rows supply the label.
 *
 * This is not a new capability: the display-value effect has always had a branch for it
 * (`optionsState.find((option) => option[optionId] === (value?.[optionId] || value))`). It simply was
 * not in the prop type, so every such caller reached it through an `as` assertion at the call site.
 */
type OptionValue = string | number;

/** Configuration for mapping option object keys to display text and unique value */
interface SelectFieldsConfig {
  text: string;
  value: string;
}

/** Individual where clause for remote data query filtering */
interface QueryWhereClause {
  isComplex?: boolean | undefined;
  ignoreCase?: boolean | undefined;
  ignoreAccent?: boolean | undefined;
  field?: string | undefined;
  operator?: string | undefined;
  value?: unknown;
}

/** Query parameters sent to remote data source */
interface RemoteDataQuery {
  where?: QueryWhereClause[] | undefined;
  [key: string]: unknown;
}

/** Shape of the response from remote data source */
interface RemoteDataResponse {
  data: { result: unknown[] };
}

/** Arguments passed to beforeOpen callback in action menu mode */
interface BeforeOpenArgs {
  items: ActionMenuItem[];
}

/** Syncfusion-compatible item type for action menus */
interface ActionMenuItem {
  [key: string]: unknown;
  id?: string | undefined;
  text?: string | undefined;
  value?: string | number | undefined;
  iconCss?: string | undefined;
  separator?: boolean | undefined;
  disabled?: boolean | undefined;
}

/** Syncfusion-compatible event args for select callback */
interface SelectEventArgs {
  item: {
    properties: ActionMenuItem;
    id?: string | undefined;
    text?: string | undefined;
  };
  element?: HTMLElement | undefined;
  /** Set to true inside the callback to cancel the selection */
  cancel?: boolean | undefined;
}

/**
 * The four callbacks that take a `T` back from the component (`onChange`, `renderOption`,
 * `groupBy`, `valueTemplate`) are declared as METHODS, not as function-typed properties. Written as
 * properties they make `T` invariant, which says a `SelectDropDownProps<Row>` is unrelated to a
 * `SelectDropDownProps<OptionBase>` — and the rows those callbacks receive are the very `options` /
 * `beforeDataBound` rows the caller supplied as `T`, so the two instantiations ARE related. That
 * relation is what lets the public component below carry the generic that `forwardRef` erases.
 */
interface SelectDropDownProps<T extends OptionBase = OptionBase> {
  // Core data props
  options?: T[] | undefined;
  /** The selected option, or just its `fields.value` id — see `OptionValue`. */
  value?: T | T[] | OptionValue | null | undefined;
  onChange?(value: T | T[] | null, args?: { cancel: boolean }): void | Promise<void>;
  beforeDataBound?: ((response: RemoteDataResponse, query: RemoteDataQuery) => T[]) | undefined;
  displayTemplate?: ((displayValue: React.ReactNode) => React.ReactNode) | undefined;
  /** Placeholder text - must be wrapped in t() for internationalization */
  placeholder?: TranslatedString | React.ReactNode | undefined;
  multiple?: boolean | undefined;
  isLoading?: boolean | undefined;
  disabled?: boolean | undefined;
  showNoOptions?: boolean | undefined;
  error?: string | undefined;
  remoteDataSource?: string | undefined;
  query?: RemoteDataQuery | undefined;
  helperText?: string | undefined;
  size?: "small" | "medium" | "large" | undefined;
  maxHeight?: number | string | undefined;
  width?: string | number | undefined;
  sx?: SxProps<Theme> | undefined;
  menuSx?: SxProps<Theme> | undefined;
  buttonType?: string | undefined;
  persistPlaceholder?: boolean | undefined;
  fields?: SelectFieldsConfig | undefined;
  menuItemSx?: SxProps<Theme> | undefined;
  renderOption?(option: T, isSelected: boolean): React.ReactNode;
  startIcon?: React.ReactNode | undefined;

  clearable?: boolean | undefined;
  searchable?: boolean | undefined;
  virtualizeList?: boolean | undefined;
  groupBy?(option: T): string;
  buttonWidth?: string | undefined;
  headerTemplate?: (() => React.ReactNode) | undefined;
  footerTemplate?: (() => React.ReactNode) | undefined;
  emptyListTemplate?: (() => React.ReactNode) | undefined;

  id?: string | undefined;
  "aria-label"?: string | undefined;
  "aria-labelledby"?: string | undefined;
  popupAlign?: "left" | "right" | "center" | undefined;
  popperWidth?: number | string | undefined;
  disableClickAwayListener?: boolean | undefined;
  open?: boolean | undefined;
  onClose?: (() => void) | undefined;
  valueTemplate?(matchedOption: T | undefined): React.ReactNode;

  // Syncfusion DropDownButtonComponent compatibility props
  children?: React.ReactNode | undefined;
  items?: ActionMenuItem[] | undefined;
  select?: ((args: SelectEventArgs) => void) | undefined;
  onSelect?: ((args: SelectEventArgs) => void) | undefined;
  cssClass?: string | undefined;
  className?: string | undefined;
  hideCaretIcon?: boolean | undefined;
  content?: string | undefined;
  iconCss?: string | undefined;
  onClick?: ((e: React.MouseEvent) => void) | undefined;
  "data-testid"?: string | undefined;
  // Additional style props for compatibility
  style?: React.CSSProperties | undefined;
  bgColor?: string | undefined;
  // Legacy Syncfusion compatibility props
  allowButtonPadding?: boolean | undefined;
  popUpWidth?: string | undefined;
  maxPopUpHeight?: string | undefined;
  beforeOpen?: ((args: BeforeOpenArgs) => void) | undefined;
}

function SelectDropDownComponent<T extends OptionBase = OptionBase>(
  props: SelectDropDownProps<T>,
  ref: React.ForwardedRef<HTMLElement>,
): React.ReactElement {
  const {
    options = null,
    value,
    onChange,
    displayTemplate,
    placeholder,
    headerTemplate,
    footerTemplate,
    remoteDataSource,
    showNoOptions = true,
    beforeDataBound,
    query,
    multiple = false,
    isLoading = false,
    disabled = false,
    error,
    helperText,
    maxHeight: maxHeightProp,
    sx,
    buttonType,
    menuSx,
    renderOption,
    startIcon,
    clearable = false,
    searchable = false,
    virtualizeList = false,
    groupBy,
    id,
    fields,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledby,
    buttonWidth,
    popupAlign = "left",
    popperWidth: popperWidthProp,
    disableClickAwayListener,
    open: controlledOpen,
    onClose,
    persistPlaceholder,
    menuItemSx,
    valueTemplate,
    // Syncfusion compatibility props
    children,
    items,
    select,
    onSelect,
    cssClass = "",
    className = "",
    hideCaretIcon,
    // content,
    onClick,
    "data-testid": dataTestId,
    style,
    bgColor,
    // Legacy Syncfusion compatibility props
    popUpWidth,
    maxPopUpHeight,
    beforeOpen,
    emptyListTemplate,
  } = props;

  // Map legacy props to their correct counterparts
  const popperWidth = popperWidthProp || popUpWidth;
  const maxHeight =
    maxHeightProp ??
    (maxPopUpHeight
      ? Number.isFinite(cssLengthToPx(maxPopUpHeight))
        ? cssLengthToPx(maxPopUpHeight)
        : cssLengthToPx("18.75rem")
      : cssLengthToPx("18.75rem"));

  // Determine if this is being used as an action menu (Syncfusion-style)
  const isActionMenu = Boolean(children || items);

  // Check if caret should be hidden (via cssClass containing e-caret-hide or explicit prop)
  const shouldHideCaret = hideCaretIcon || cssClass.includes("e-caret-hide");

  // Use items if provided, otherwise use options
  const effectiveOptions: OptionBase[] | null = items || options;

  // Default fields for action menu items

  const effectiveFields: SelectFieldsConfig = fields || { text: "text", value: "id" };
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchText, setSearchText] = useState("");
  const open = controlledOpen === undefined ? Boolean(anchorEl) : Boolean(anchorEl) && controlledOpen;
  const [optionsState, setOptionsState] = useState<OptionBase[]>([]);
  const optionId = effectiveFields.value;
  const generatedId = useId();
  const uniqueId = useMemo(() => id || `select-dropdown-${generatedId}`, [id, generatedId]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading]);

  useEffect(() => {
    if (controlledOpen === false) {
      setAnchorEl(null);
      setSearchText("");
    }
  }, [controlledOpen]);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    // Call custom onClick handler if provided
    if (onClick) {
      onClick(event);
    }

    if (!disabled) {
      if (anchorEl) {
        // Currently open, close it
        setAnchorEl(null);
      } else {
        // About to open - call beforeOpen callback to allow item modification
        if (beforeOpen && isActionMenu) {
          const itemsCopy = optionsState.map((item) => ({ ...item })) as ActionMenuItem[];
          beforeOpen({ items: itemsCopy });
          setOptionsState(itemsCopy);
        }
        setAnchorEl(event.currentTarget);
      }
    }
  };

  useEffect(() => {
    try {
      if (Array.isArray(effectiveOptions)) {
        setOptionsState(effectiveOptions);
      } else if (!remoteDataSource && !query) {
        // Only throw error if not an action menu (action menus may have empty items)
        if (!isActionMenu) {
          throw new Error("options should be an array or use remote data source");
        }
      }
    } catch (e) {
      console.log(e);
      // Deliberately left floating: this catch has already consumed the error, and re-surfacing it
      // as an UNHANDLED rejection is what reports it to Sentry as `handled: false`. Calling
      // `captureException` here instead would only demote the same event to `handled: true`.
      // Reported, not re-thrown. `void Promise.reject(...)` was wrong twice over: it creates a
      // NEW rejection whose stack points here rather than at the real failure, and it trips this
      // repo's own `catch-handler/require-promise-reject` rule, which accepts captureException.
      Sentry.captureException(e);
    }
  }, [effectiveOptions, isActionMenu]);

  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchOptions = async (queryParam: RemoteDataQuery | undefined = query) => {
    let optionsData: OptionBase[];
    if (!queryParam || !remoteDataSource) return;
    setLoading(true);
    // Dynamic remoteDataSource URL - endpoint not known at compile time
    const response = await axiosWrapper.post<{ result: OptionBase[] }>(remoteDataSource, {
      value: { ...queryParam, params: queryParam },
    });
    if (!mountedRef.current) return;
    if (beforeDataBound) {
      optionsData = beforeDataBound(response as RemoteDataResponse, queryParam);
    } else {
      optionsData = response.data.result;
    }
    // A dynamic remoteDataSource (or a custom beforeDataBound) can yield a
    // non-array payload (missing/empty `result`). optionsState is relied upon
    // throughout this component as an array, so normalise at the ingestion
    // boundary instead of guarding every consumer.
    setOptionsState(Array.isArray(optionsData) ? optionsData : []);
    setLoading(false);
  };

  useEffect(() => {
    // Fire-and-forget from an effect; `fetchOptions` drives its own loading flag.
    void fetchOptions();
  }, [query]);

  const handleClose = () => {
    setAnchorEl(null);
    setSearchText("");
    onClose?.();
  };

  const standbyRef = useRef<HTMLElement | null>(null);

  const buttonRef = ref || standbyRef;

  // The forwarded ref is declared over `HTMLElement` because it is attached to several different
  // slots; a callback ref hands the concrete node to whichever ref shape the caller passed instead
  // of asserting the narrow element type onto the wide one (same pattern as MenuItemComponent).
  const setActionButtonRef = useCallback(
    (node: HTMLButtonElement | null) => {
      if (typeof buttonRef === "function") {
        buttonRef(node);
      } else {
        buttonRef.current = node;
      }
    },
    [buttonRef],
  );

  /**
   * `optionsState` holds `OptionBase` rows because it is fed from either `props.options` (`T[]`),
   * `props.beforeDataBound` (`T[]`) or the action-menu `props.items` (`ActionMenuItem[]`). Every
   * `T`-typed callback (`onChange`, `renderOption`, `groupBy`, `valueTemplate`) is reachable only in
   * non-action-menu mode, where the rows are exactly the ones the caller declared as `T`.
   */
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- see the invariant above: rows reaching a `T`-typed callback originate from `props.options` / `props.beforeDataBound`, both already `T[]`
  const asOption = (option: OptionBase): T => option as T;

  const handleSelect = async (option: OptionBase, element?: HTMLElement) => {
    if (option.disabled) return;

    // For action menu mode (Syncfusion compatibility)
    if (isActionMenu) {
      const menuItem = option as ActionMenuItem;
      const selectArgs: SelectEventArgs = {
        item: {
          properties: menuItem,
          id: menuItem.id,
          text: menuItem.text,
        },
        element,
        cancel: false,
      };

      // Call Syncfusion-compatible callbacks - either may set cancel=true to abort
      if (select) {
        select(selectArgs);
      }
      if (onSelect) {
        onSelect(selectArgs);
      }

      if (selectArgs.cancel) return;

      handleClose();
      return;
    }

    // Standard select dropdown mode
    if (multiple) {
      const currentValues: T[] = Array.isArray(value) ? value : [];
      const alreadySelected = currentValues.some((v) => v[optionId] === option[optionId]);
      const newValue: T[] = alreadySelected
        ? currentValues.filter((v) => v[optionId] !== option[optionId])
        : [...currentValues, asOption(option)];
      const changeArgs = { cancel: false };
      if (onChange) await onChange(newValue, changeArgs);
      if (changeArgs.cancel) return;
    } else {
      const changeArgs = { cancel: false };
      if (onChange) await onChange(asOption(option), changeArgs);
      if (!value && !changeArgs.cancel) {
        const text = option[effectiveFields.text];
        if (text != null) {
          setDisplayValue(toDisplayNode(text));
        }
      }
      handleClose();
    }
  };

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    const changeArgs = { cancel: false };
    // Typed empty selection, so the multi-select clear payload needs no assertion.
    const cleared: T[] = [];
    // Fire-and-forget: the clear button's handler is synchronous and reads nothing back. (The two
    // `onChange` calls in `handleSelect` await it because the display value is set afterwards.)
    if (onChange) void onChange(multiple ? cleared : null, changeArgs);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
    if (remoteDataSource) {
      const searchQuery: RemoteDataQuery = {
        ...query,
        where: [
          {
            isComplex: false,
            ignoreCase: true,
            ignoreAccent: false,
            field: effectiveFields.text,
            operator: "startswith",
            value: event.target.value,
          },
        ],
      };
      // Fire-and-forget: the search box stays responsive and the list re-renders when it lands.
      void fetchOptions(searchQuery);
    }
  };

  /** The selected option's id, whether `value` is the whole option or just the id itself. */
  const selectedOptionId = (selected: T | OptionValue) =>
    typeof selected === "object" ? selected[optionId] : selected;

  const isSelected = useCallback(
    (option: OptionBase) => {
      if (!value) return false;
      if (Array.isArray(value)) {
        return value.some((v) => v[optionId] === option[optionId]);
      }
      return selectedOptionId(value) === option[optionId];
    },
    [value, optionId],
  );

  const [displayValue, setDisplayValue] = useState<React.ReactNode>(placeholder || t("Select an option"));

  useEffect(() => {
    // Skip display value updates for action menu mode
    if (isActionMenu) return;

    const translatedPlaceholder: React.ReactNode = placeholder || t("Select an option");

    if (!value || !optionsState.length) {
      setDisplayValue(translatedPlaceholder);
      return;
    }
    if (persistPlaceholder) {
      return;
    }

    if (Array.isArray(value)) {
      const names = value
        .map((v) => v[effectiveFields.text])
        .filter(Boolean)
        .join(", ");
      setDisplayValue(names || translatedPlaceholder);
    } else {
      // Only a whole option carries the label; an id-only `value` falls straight through to the
      // lookup below, which resolves the label from the fetched rows.
      const text = typeof value === "object" ? value[effectiveFields.text] : undefined;
      if (text != null) {
        setDisplayValue(toDisplayNode(text));
      } else {
        const matched = optionsState.find((option) => option[optionId] === selectedOptionId(value));
        setDisplayValue(toDisplayNode(matched?.[effectiveFields.text]) || translatedPlaceholder);
      }
    }
  }, [value, optionsState, isActionMenu, effectiveFields.text, optionId]);

  const filteredOptions = useMemo(() => {
    // Filter out separator items for processing (they're rendered separately)
    const nonSeparatorOptions = optionsState.filter((option) => !option.separator);
    if (!searchText || Boolean(remoteDataSource)) return nonSeparatorOptions;
    return nonSeparatorOptions.filter((option) => {
      const optionText = option[effectiveFields.text];
      if (typeof optionText === "string") {
        const lowerSearchText = searchText.toLowerCase();
        return optionText.toLowerCase().includes(lowerSearchText);
      } else {
        return optionText === searchText;
      }
    });
  }, [optionsState, searchText, effectiveFields.text, remoteDataSource]);

  const groupedOptions = useMemo(() => {
    if (!groupBy) return { ungrouped: filteredOptions };

    return filteredOptions.reduce(
      (acc: Record<string, OptionBase[]>, option) => {
        const groupKey = String(option.group || groupBy(asOption(option)) || "ungrouped");
        if (!acc[groupKey]) {
          acc[groupKey] = [];
        }
        acc[groupKey].push(option);
        return acc;
      },
      {} as Record<string, OptionBase[]>,
    );
  }, [filteredOptions, groupBy]);

  const renderMenuItem = useCallback(
    (option: OptionBase, index: number, style?: React.CSSProperties) => {
      // Handle separator items
      if (option.separator) {
        return <Divider key={`separator-${index}`} sx={{ my: "0.25rem" }} />;
      }

      const itemSelected = isSelected(option);

      if (renderOption) {
        return (
          <MenuItemComponent
            key={toOptionKey(option[optionId], index)}
            selected={itemSelected}
            onClick={(e: React.MouseEvent<HTMLElement>) => {
              e.stopPropagation();
              // Fire-and-forget: the click handler has a void contract and `handleSelect` owns the
              // whole selection sequence, including closing the menu.
              void handleSelect(option, e.currentTarget);
            }}
            disabled={Boolean(option.disabled)}
            style={style}
            sx={{ opacity: option.disabled ? 0.5 : 1, ...menuItemSx }}
          >
            {renderOption(asOption(option), itemSelected)}
          </MenuItemComponent>
        );
      }

      return (
        <MenuItemComponent
          key={toOptionKey(option[optionId], index)}
          selected={itemSelected}
          onClick={(e: React.MouseEvent<HTMLElement>) => {
            e.stopPropagation();
            // Fire-and-forget, as in the `renderOption` branch above.
            void handleSelect(option, e.currentTarget);
          }}
          disabled={Boolean(option.disabled)}
          style={style}
          sx={{ opacity: option.disabled ? 0.5 : 1 }}
        >
          {multiple && <CheckBoxComponent checked={itemSelected} disabled={Boolean(option.disabled)} />}
          <ListItemText
            primary={
              <Typography
                noWrap
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                <ToolTipComponent
                  slotProps={{ popper: { sx: { zIndex: 10000 } } }}
                  content={untranslated(String(option[effectiveFields.text] ?? ""))}
                >
                  {toDisplayNode(option[effectiveFields.text])}
                </ToolTipComponent>
              </Typography>
            }
          />
        </MenuItemComponent>
      );
    },
    [multiple, isSelected, renderOption, effectiveFields.text, handleSelect, optionId, menuItemSx],
  );

  const getPlacement = () => {
    if (popupAlign === "left") return "bottom-start";
    if (popupAlign === "right") return "bottom-end";
    return "bottom";
  };

  const getVirtualListHeight = () => {
    if (typeof maxHeight === "number") {
      const searchBarHeight = searchable ? 70 : 0;
      return maxHeight - searchBarHeight;
    }
    return 250;
  };

  const renderMenuContent = () => {
    if (loading) {
      return (
        <MenuItem sx={{ paddingBottom: "1.25rem" }} disabled>
          <CircularProgress size={20} style={{ marginInlineEnd: "0.5rem" }} />
          {t("Loading...")}
        </MenuItem>
      );
    }

    if (filteredOptions.length === 0) {
      if (emptyListTemplate) {
        return emptyListTemplate();
      }
      return (
        showNoOptions && (
          <MenuItem disabled>
            <Text>{t("No options available")}</Text>
          </MenuItem>
        )
      );
    }

    if (virtualizeList) {
      return (
        <div style={{ height: getVirtualListHeight() }}>
          <AutoSizer>
            {({ height, width }) => (
              <List
                height={height}
                width={width}
                rowCount={filteredOptions.length}
                rowHeight={48}
                rowRenderer={({ index, style }) => renderMenuItem(filteredOptions[index]!, index, style)}
              />
            )}
          </AutoSizer>
        </div>
      );
    }

    if (groupBy) {
      return Object.entries(groupedOptions).map(([group, groupOptions]) => (
        <React.Fragment key={group}>
          {group !== "ungrouped" && (
            <MenuItem
              disabled
              style={{
                fontWeight: "bold",
                opacity: 0.7,
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              }}
            >
              {group}
            </MenuItem>
          )}
          {groupOptions.map((option, index) => renderMenuItem(option, index))}
        </React.Fragment>
      ));
    }

    // For action menus, render items including separators in original order
    if (isActionMenu) {
      return optionsState.map((option, index) => renderMenuItem(option, index));
    }

    return filteredOptions.map((option, index) => renderMenuItem(option, index));
  };

  // The row handed to `valueTemplate`, resolved the same way the display-value effect resolves it:
  // `value` is either the whole option or just its `fields.value` id, and a multi-select array has
  // no single row to template.
  const matchedValue =
    value == null || Array.isArray(value)
      ? undefined
      : optionsState.find((option) => option[optionId] === selectedOptionId(value));
  const matchedValueOption = matchedValue ? asOption(matchedValue) : undefined;

  // Combine cssClass and className for action menu compatibility
  const combinedClassName = `${cssClass} ${className}`.trim();

  return (
    <ClickAwayListener
      mouseEvent="onMouseDown"
      onClickAway={() => {
        if (!disableClickAwayListener) {
          handleClose();
        }
      }}
    >
      <div
        style={{
          width: isActionMenu ? "auto" : buttonWidth ? buttonWidth : "100%",
          backgroundColor: bgColor,
          ...style,
        }}
        className={combinedClassName}
        data-testid={dataTestId}
      >
        {/* Action menu mode: children as trigger */}
        {isActionMenu && children ? (
          <button
            type="button"
            onClick={handleOpen}
            disabled={disabled}
            className="flex items-center justify-center cursor-pointer bg-transparent border-none p-0"
            style={{ opacity: disabled ? 0.5 : 1 }}
            ref={setActionButtonRef}
            aria-haspopup="menu"
            aria-expanded={open}
            aria-controls={open ? `${uniqueId}-menu` : undefined}
          >
            {children}
          </button>
        ) : displayTemplate ? (
          <button className="w-full h-full" onClick={handleOpen}>
            {" "}
            {displayTemplate(displayValue)}{" "}
          </button>
        ) : (
          <ButtonComponent
            onClick={handleOpen}
            buttonType={buttonType}
            disabled={disabled}
            sx={{
              textAlign: "left",
              width: buttonWidth ? buttonWidth : "100%",
              justifyContent: "space-between",
              maxWidth: buttonWidth ? buttonWidth : "100%",
              minWidth: "0 !important",
              "& > span.font-semibold": {
                overflow: "hidden",
                minWidth: 0,
                flex: "1 1 0",
              },
              ...(sx || {}),
            }}
            // @ts-expect-error -- an `HTMLElement` ref is forwarded to a MUI slot typed `Ref<HTMLButtonElement>`. Same element-type mismatch as `ToggleSwitchComponent`; the ref is created generically and used for several slots.
            ref={buttonRef}
            startIcon={startIcon}
            endIcon={
              shouldHideCaret ? null : (
                <>
                  {clearable && value && (!Array.isArray(value) || value.length !== 0) && (
                    <IconButton sx={{ padding: "0.125rem" }} onClick={handleClear}>
                      <CloseIcon
                        fontSize="inherit"
                        style={{ marginInlineEnd: 0, cursor: "pointer", alignSelf: "center", height: "100%" }}
                      />
                    </IconButton>
                  )}
                  {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                </>
              )
            }
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-controls={open ? `${uniqueId}-menu` : undefined}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledby}
            id={uniqueId}
          >
            {loading ? (
              <div style={{ display: "flex", alignItems: "center" }}>
                <CircularProgress size={20} style={{ marginInlineEnd: "0.5rem" }} />
                {t("Loading...")}
              </div>
            ) : valueTemplate ? (
              valueTemplate(matchedValueOption)
            ) : (
              displayValue
            )}
          </ButtonComponent>
        )}

        <Popper
          id={`${uniqueId}-popper`}
          open={open}
          anchorEl={anchorEl}
          sx={{
            // For action menus, use auto width; for regular dropdowns, match button width
            width: isActionMenu ? "auto" : popperWidth || buttonWidth || "auto",
            maxWidth: isActionMenu ? "none" : popperWidth || buttonWidth || "auto",
            minWidth: isActionMenu ? "9.375rem" : popperWidth || buttonWidth || "auto",
            border: "1px solid #ECF3FE",
            boxShadow: "0.25rem 0.25rem 0.9375rem rgba(65, 92, 171, 0.15)",
            zIndex: 1500,
            background: "var(--surface-white)",
            maxHeight: `${Number(maxHeight) / 16}rem`,
            overflow: "auto",
            ...(menuSx || {}),
          }}
          placement={getPlacement()}
          modifiers={[
            {
              name: "sameWidth",
              // Disable sameWidth modifier for action menus
              enabled: !isActionMenu,
              phase: "beforeWrite",
              requires: ["computeStyles"],
              fn: ({ state }) => {
                // popperWidth may be a number, "Npx", or "Nrem"; normalize to px for popper.
                const resolved = cssLengthToPx(popperWidth);
                const widthPx = Number.isFinite(resolved) ? resolved : state.rects.reference.width;
                // eslint-disable-next-line no-px/no-px -- popper width must match reference element's DOM-measured px width exactly; rem conversion risks subpixel drift if root font-size != 16px
                state.styles.popper = { ...(state.styles.popper ?? {}), width: `${widthPx}px` };
              },
            },
          ]}
        >
          <>
            {headerTemplate && <div className="py-4 sticky flex top-0 w-full z-10 bg-white">{headerTemplate()}</div>}
            {searchable && (
              <div style={{ padding: "1rem", position: "sticky", top: 0, backgroundColor: "white", zIndex: 1 }}>
                <TextFieldComponent
                  placeholder={t("Search...")}
                  size="small"
                  value={searchText}
                  onChange={handleSearchChange}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    },
                  }}
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                />
              </div>
            )}

            {/* MenuList provides the MenuListContext + RovingTabIndexContext that MUI v9's
                MenuItem now *requires* (it throws without them). The search field / header /
                footer stay outside so MenuList typeahead & arrow-key nav don't hijack the
                search input. disablePadding keeps spacing identical to the pre-wrap layout. */}
            <MenuList disablePadding sx={{ outline: "none" }}>
              {renderMenuContent()}
            </MenuList>
            {footerTemplate && <div className="py-4 flex sticky bottom-0 w-full bg-white z-10">{footerTemplate()}</div>}
          </>
        </Popper>

        {(error || helperText) && (
          <div
            style={{
              color: error ? "red" : "rgba(0, 0, 0, 0.6)",
              marginTop: "0.25rem",
              fontSize: "0.75rem",
            }}
          >
            {error || helperText}
          </div>
        )}
      </div>
    </ClickAwayListener>
  );
}

/**
 * `forwardRef` erases the render function's type parameter, so the public component is declared with
 * the generic call signature it actually has and the erased result is assigned into it. The return
 * is `ReactNode` because that is what the erased `ForwardRefExoticComponent` call signature returns,
 * not the narrower `ReactElement | null` the render function happens to produce.
 */
interface SelectDropDownType {
  <T extends OptionBase = OptionBase>(
    props: SelectDropDownProps<T> & React.RefAttributes<HTMLElement>,
  ): React.ReactNode;
  displayName?: string | undefined;
}

const SelectDropDown: SelectDropDownType = forwardRef(SelectDropDownComponent);

SelectDropDown.displayName = "SelectDropDown";
export default SelectDropDown;
export type {
  SelectDropDownProps,
  ActionMenuItem,
  SelectEventArgs,
  SelectFieldsConfig,
  RemoteDataQuery,
  RemoteDataResponse,
  BeforeOpenArgs,
  OptionBase,
  QueryWhereClause,
};
